import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Popover from '@mui/material/Popover';
import { anchorRef, bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import {
  FilterButton,
  SearchForm,
  SearchFormProps,
  TableContainer,
  TableHeader,
  Typography,
} from '@occc-app/design-system';
import { Published_Status_Type } from '@occc/graphql';
import { deletePaginationParams, usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { useQueryParam } from '@occc-app/hooks/useQueryParam';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { useGetCampaignComponents } from '@occc-app/dataAccessLayer/campaign/queries';

import { Stack } from '@mui/material';
import { CampaignComponentsFilterFormValues } from './types';
import { CampaignComponentsTable } from './CampaignComponentsTable';
import { CampaignComponentsFilterForm } from './CampaignComponentsFilterForm';
import { mapCampaignComponent } from './utils';
import {
  clearOtherCampaignComponentsFilters,
  loadCampaignComponentsFilters,
  saveCampaignComponentsFilters,
} from './campaignComponentsFiltersStorage';

const PUBLISHED_STATUS_PARAM = 'publishedStatus';

// Shown by default (and on reset) when the URL has no explicit selection.
export const DEFAULT_PUBLISHED_STATUSES: Published_Status_Type[] = [
  Published_Status_Type.Published,
  Published_Status_Type.Unpublished,
  Published_Status_Type.Expired,
];

const ALL_PUBLISHED_STATUSES: Published_Status_Type[] = [
  Published_Status_Type.Published,
  Published_Status_Type.Unpublished,
  Published_Status_Type.Expired,
  Published_Status_Type.Archived,
];

// True when the list is narrowed (including the default, which excludes archived).
export const isCampaignComponentsFiltered = (statuses: Published_Status_Type[]) =>
  statuses.length > 0 && statuses.length < ALL_PUBLISHED_STATUSES.length;

type CampaignComponentsTableContainerProps = { campaignId: string };

export const CampaignComponentsTableContainer = ({
  campaignId,
}: CampaignComponentsTableContainerProps) => {
  const navigate = useNavigate();

  const isUserPermittedToDeleteCampaignComponent = useIsUserPermittedTo(
    Feature.CAMPAIGN_COMPONENT_DELETE,
  );

  const [search, setSearch] = useSearchParams();
  const [paginationInfo, onPageChange] = usePaginationParams();

  useEffect(() => {
    clearOtherCampaignComponentsFilters(campaignId);
  }, [campaignId]);

  // The URL param is the source of truth; storage only covers the routes that rebuild
  // the campaign URL from scratch and so drop the query string - notably the breadcrumb
  // link back from a component page.
  const [publishedStatusFilter, setPublishedStatusFilterParam] = useQueryParam<
    Published_Status_Type[]
  >(
    (searchParams) => {
      const raw = searchParams.get(PUBLISHED_STATUS_PARAM);
      if (raw === '') {
        return [];
      }
      if (raw === null) {
        return (
          loadCampaignComponentsFilters(campaignId)?.publishedStatus ?? DEFAULT_PUBLISHED_STATUSES
        );
      }
      return raw.split(',').filter(Boolean) as Published_Status_Type[];
    },
    (value, searchParams) => {
      if (value.length) {
        searchParams.set(PUBLISHED_STATUS_PARAM, value.join(','));
      } else {
        searchParams.delete(PUBLISHED_STATUS_PARAM);
      }

      deletePaginationParams(searchParams);
    },
  );

  const setPublishedStatusFilter = (value: Published_Status_Type[]) => {
    saveCampaignComponentsFilters(campaignId, { publishedStatus: value });
    setPublishedStatusFilterParam(value);
  };

  const { loading, error, data, paginationProps } = useGetCampaignComponents({
    filter: {
      search,
      campaignIdEquals: campaignId,
      ...(publishedStatusFilter.length ? { publishedStatusIncludes: publishedStatusFilter } : {}),
    },
    paginationInfo,
    onPageChange,
  });

  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const popoverProps = {
    ...bindPopover({
      ...popupState,
      isOpen: popupState.isOpen,
    }),
  };

  const onRowClick = (id: string) => navigate(id);

  const campaignComponents = data?.map(mapCampaignComponent) || null;

  const onSearch: SearchFormProps['onSubmit'] = ({ search }) => {
    setSearch(search);
  };

  const onFilterReset = () => {
    setPublishedStatusFilter(DEFAULT_PUBLISHED_STATUSES);
    popoverProps.onClose();
  };

  const onFilterSubmit = ({ publishedStatus }: CampaignComponentsFilterFormValues) => {
    setPublishedStatusFilter(publishedStatus);
    popoverProps.onClose();
  };

  const renderActions = () => (
    <Stack direction='row' alignItems='center' gap={1}>
      <SearchForm onSubmit={onSearch} initialValues={{ search }} />
      <FilterButton
        {...bindTrigger(popupState)}
        ref={anchorRef(popupState)}
        showBadge={isCampaignComponentsFiltered(publishedStatusFilter)}
      />
    </Stack>
  );

  return (
    <TableContainer>
      <TableHeader
        title={
          <Typography variant='h6' component='h3'>
            Campaign Components
          </Typography>
        }
        disableTitleTypography
        actions={renderActions()}
      />
      <CampaignComponentsTable
        campaignComponents={campaignComponents}
        loading={loading}
        error={error}
        paginationProps={paginationProps}
        onRowClick={onRowClick}
        isActionsColumnVisible={isUserPermittedToDeleteCampaignComponent}
      />
      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
        PaperProps={{ sx: { width: 300 } }}
      >
        <CampaignComponentsFilterForm
          onSubmit={onFilterSubmit}
          onReset={onFilterReset}
          initialValues={{ publishedStatus: publishedStatusFilter }}
        />
      </Popover>
    </TableContainer>
  );
};
